import React, { useState, useEffect, useContext, useRef } from "react";
import ProfileHome from "./ProfileHome";
import Details from "./Details";
import FAQPage from "./FAQPage";
import Forum from "./Forum";
import ContactUs from "./ContactUs";
import { useNavigate, Routes, Route } from "react-router-dom";
import { Button, Tooltip } from "@mui/material";
import { AuthContext } from "../../context/AuthContext";
import { GeneralContext } from "../../context/GeneralContext";
import AdminPage from "./AdminPage";
import { api } from "../../utils/authActions";
import "./profilePages.scss";
import TestPage from "./TestPage";
import { WebSocketContext } from "../../context/WebSocketContext";
import { ReadyState } from "react-use-websocket";
import { hasProperty } from "../../utils/helpers";
import BroadcastModal from "../../components/commonComponents/BroadcastModal";
import { DashboardContext } from "../../context/DashboardContext";
import { GraphContext } from "../../context/GraphContext";
import ErrorBoundary from "../../errorHandling/ErrorBoundary";
import CrashPage from "../../errorHandling/CrashPage";
import AlphaTestersManual from "../legal/AlphaTestersManual";
import PrivacyPage from "../legal/PrivacyPage";
import TermsOfUse from "../legal/TermsOfUse";
import TestersAgreement from "../legal/TestersAgreement";
import Feedback from "../Feedback";

function AdminSystem() {
  const navigate = useNavigate();
  const {
    isAdmin,
    authToken,
    isAuthReady,
    setPackages,
    setCurrentUser,
    //isFirstLogin,
    resetAuth,
    setIsFirstLogin,
    isActive,
  } = useContext(AuthContext);
  const {
    lastJsonMessage,
    readyState,
    sendMessage,
    authSent,
    setAuthSent,
    resetWebSocketContext,
  } = useContext(WebSocketContext);
  const { resetDashboardContext } = useContext(DashboardContext);
  const { resetGraphContext } = useContext(GraphContext);
  const {
    setBroadcastMessage,
    setActivePolls,
    setPollToSolve,
    recordedErrorLog,
  } = useContext(GeneralContext);
  const [username, setUsername] = useState("");
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const adminSytemPageRef = useRef();

  useEffect(() => {
    if (
      isAuthReady &&
      authToken !== "" &&
      ReadyState.OPEN === readyState &&
      !authSent
    ) {
      sendMessage("Token: " + authToken);
      setAuthSent(true);
    }
  }, [readyState, isAuthReady]);

  useEffect(() => {
    // These are not Admin API calls, but they are needed to load the page
    if (isAuthReady) {
      if (authToken === "") {
        navigate("/login");
      }

      // Sequential API calls to avoid race conditions
      const loadUserData = async () => {
        try {
          await new Promise((resolve) => setTimeout(resolve, 100)); // First: Get user data (most critical)
          try {
            const userResponse = await api.getMe(authToken);
            setUsername(userResponse.data.full_name);
            setCurrentUser(userResponse.data);
          } catch (err) {
            if (
              hasProperty(err, "response") &&
              hasProperty(err.response, "status") &&
              err.response.status === 403
            ) {
              recordedErrorLog(
                `THERE WAS AN ERROR WITH USER DATA LOADING: CREDENTIALS`,
                err,
                true,
                "Your credentials could not be validated. You can still save your session. Please log out and log in again or contact support."
              );
              return;
            }
            const errorDetails = hasProperty(err, "response")
              ? `Backend response: ${JSON.stringify(err.response, null, 2)}`
              : "No backend response available";
            recordedErrorLog(
              `THERE WAS AN ERROR WITH USER DATA LOADING: ${errorDetails}`,
              err,
              true,
              "Unexpected error occurred while loading user details. Please try again or contact support."
            );
            return;
          }
          // Small delay to prevent overwhelming the backend
          await new Promise((resolve) => setTimeout(resolve, 100));
          try {
            // Second: Get packages
            const packagesResponse = await api.getPackages(authToken);
            setPackages(packagesResponse.data);
          } catch (err) {
            const errorDetails = hasProperty(err, "response")
              ? `Backend response: ${JSON.stringify(err.response, null, 2)}`
              : "No backend response available";
            recordedErrorLog(
              `THERE WAS AN ERROR WITH USER DATA LOADING: ${errorDetails}`,
              err,
              true,
              "Unexpected error occurred while loading packages. Please try again or contact support."
            );
          }
          // Small delay
          await new Promise((resolve) => setTimeout(resolve, 100));

          // Third: Get active polls
          try {
            const pollsResponse = await api.getActivePolls(authToken);
            setActivePolls(pollsResponse.data);
            if (pollsResponse.data.length > 0) {
              setPollToSolve(pollsResponse.data[0]);
            }
            // else {
            //   if (isActive && isFirstLogin) {
            //     navigate("/dashboard");
            //   }
            // }
          } catch (err) {
            const errorDetails = hasProperty(err, "response")
              ? `Backend response: ${JSON.stringify(err.response, null, 2)}`
              : "No backend response available";
            recordedErrorLog(
              `THERE WAS AN ERROR WITH ACTIVE POLLS: ${errorDetails}`,
              err,
              false
            );
          }
          setIsFirstLogin(false);
        } catch (err) {
          const errorDetails = hasProperty(err, "response")
            ? `Backend response: ${JSON.stringify(err.response, null, 2)}`
            : "No backend response available";
          recordedErrorLog(
            `THERE WAS AN ERROR WITH USER DATA LOADING: ${errorDetails}`,
            err,
            true,
            "Unexpected error occurred while loading user data. Please try again or contact support."
          );
        }
      };

      loadUserData();
    }
  }, [isAuthReady]);

  useEffect(() => {
    if (lastJsonMessage) {
      if (hasProperty(lastJsonMessage, "Broadcast")) {
        setBroadcastMessage(lastJsonMessage.Broadcast);
        setBroadcastOpen(true);
      }
    }
  }, [lastJsonMessage]);

  const handleLogout = () => {
    try {
      sessionStorage.clear();
      resetDashboardContext();
      resetAuth();
      resetGraphContext();
      resetWebSocketContext();
      navigate("/login");
    } catch (error) {
      recordedErrorLog("Error handling logout: ", error);
    }
  };

  const handleCloseIconClick = () => {
    setBroadcastOpen(false);
  };

  return (
    <ErrorBoundary
      fallbackComponent={CrashPage}
      fallbackProps={{
        text: "User Home System failure.",
      }}
    >
      <div className="adminSystemPage" ref={adminSytemPageRef}>
        <div className="sidePannel">
          <div className="userArea">
            Welcome,
            <br />
            <div className="userName">{username}</div>
          </div>
          <div className="pagesArea">
          <div className="sidePannelBottom">
            <div className="ToSpeqqleButton">
              <Tooltip 
                title={isActive <= 0 ? "Please subscribe to a plan first. This can be done in the Home tab." : ""}
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/dashboard")}
                    className="toSpeqqleButton"
                    id="user-home-to-speqqle-button"
                    disabled={isActive <= 0}
                  >
                    To Speqqle
                  </Button>
                </span>
              </Tooltip>
            </div>
          </div>
            <div className="linkContainer">
              <div className="link" onClick={() => navigate("/profile")}>
                Home
              </div>
              <div className="link" onClick={() => navigate("/manual")}>
                Manual
              </div>
              <div
                className="link"
                onClick={() => navigate("/profile/details")}
                id="details-navigation-button"
              >
                User Details
              </div>
              <div
                className="link"
                onClick={() => navigate("/profile/privacy-policy")}
              >
                Privacy Policy
              </div>
              <div
                className="link"
                onClick={() => navigate("/profile/terms-of-use")}
              >
                Terms of Use
              </div>
              {/*<div
                className="link"
                onClick={() => navigate("/profile/test-agreement")}
              >
                Testing Agreement
              </div>*/}
              {/* <div className="link" onClick={() => navigate("/profile/faq")}>
                F.A.Q.
              </div> */}
              {/* <div className="link" onClick={() => navigate("/profile/forum")}>
              Forum
            </div> */}
              <div
                className="link"
                onClick={() => navigate("/profile/feedback")}
              >
                Feedback
              </div>
              <div
                className="link"
                onClick={() => navigate("/profile/contact")}
                id="contact-navigation-button"
              >
                Contact
              </div>
              {isAdmin && (
                <div
                  className="link"
                  onClick={() => navigate("/profile/admin")}
                >
                  Admin
                </div>
              )}
            </div>
          </div>
          <div className="sidePannelBottom">
            <div className="logout">
              <Button
                variant="outlined"
                onClick={() => handleLogout()}
                className="logoutButton"
                id="user-home-logout-button"
              >
                Log out
              </Button>
            </div>
          </div>
        </div>
        <div className="adminSystemContent">
          <Routes>
            <Route
              path="/"
              element={
                <ErrorBoundary
                  fallbackComponent={CrashPage}
                  fallbackProps={{
                    text: "User Home failure.",
                  }}
                >
                  <ProfileHome />
                </ErrorBoundary>
              }
            />
            <Route
              path="feedback"
              element={
                <ErrorBoundary
                  fallbackComponent={CrashPage}
                  fallbackProps={{
                    text: "Beta testers feedback failure.",
                  }}
                >
                  <Feedback />
                </ErrorBoundary>
              }
            />
            <Route
              path="manual"
              element={
                <ErrorBoundary
                  fallbackComponent={CrashPage}
                  fallbackProps={{
                    text: "Beta testers manual failure.",
                  }}
                >
                  <AlphaTestersManual />
                </ErrorBoundary>
              }
            />
            <Route
              path="privacy-policy"
              element={
                <ErrorBoundary
                  fallbackComponent={CrashPage}
                  fallbackProps={{
                    text: "Privacy policy page failure.",
                  }}
                >
                  <PrivacyPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="terms-of-use"
              element={
                <ErrorBoundary
                  fallbackComponent={CrashPage}
                  fallbackProps={{
                    text: "Terms of Use page failure.",
                  }}
                >
                  <TermsOfUse />
                </ErrorBoundary>
              }
            />
            <Route
              path="test-agreement"
              element={
                <ErrorBoundary
                  fallbackComponent={CrashPage}
                  fallbackProps={{
                    text: "Test agreement page failure.",
                  }}
                >
                  <TestersAgreement noOptIn />
                </ErrorBoundary>
              }
            />
            <Route
              path="details"
              element={
                <ErrorBoundary
                  fallbackComponent={CrashPage}
                  fallbackProps={{
                    text: "User Details failure.",
                  }}
                >
                  <Details />
                </ErrorBoundary>
              }
            />
            <Route
              path="faq"
              element={
                <ErrorBoundary
                  fallbackComponent={CrashPage}
                  fallbackProps={{
                    text: "F.A.Q. page failure.",
                  }}
                >
                  <FAQPage />
                </ErrorBoundary>
              }
            />
            <Route path="forum" element={<Forum />} />
            <Route
              path="contact"
              element={
                <ErrorBoundary
                  fallbackComponent={CrashPage}
                  fallbackProps={{
                    text: "Contact page failure.",
                  }}
                >
                  <ContactUs />
                </ErrorBoundary>
              }
            />
            <Route
              path="admin"
              element={
                <ErrorBoundary
                  fallbackComponent={CrashPage}
                  fallbackProps={{
                    text: "Administrator page failure.",
                  }}
                >
                  <AdminPage />
                </ErrorBoundary>
              }
            />
            <Route path="test" element={<TestPage />} />
          </Routes>
        </div>
        <BroadcastModal
          elementRef={adminSytemPageRef}
          isOpen={broadcastOpen}
          setIsOpen={setBroadcastOpen}
          handleClose={handleCloseIconClick}
        />
      </div>
    </ErrorBoundary>
  );
}

export default AdminSystem;
